import React from "react";
import { FileIcon, defaultStyles } from "react-file-icon";
import { toast } from "sonner";

import placeholderImg from "@/assets/images/empty.jpg";
import Avatar from "@/components/reusables/Avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import * as Sheet from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { allowedImageTypes, imgBaseUrl } from "@/constants/constants";
import { REQUEST_METHODS } from "@/constants/global-constants";
import { SOCKET_KEYS, SocketEventType } from "@/constants/socket.constant";
import {
  SupportConstants,
  SupportTicketKeys,
} from "@/constants/support.constants";
import { useApiInvalidator } from "@/hooks/useApiInvalidator";
import useQueryID from "@/hooks/useQueryId";
import { useSocket } from "@/hooks/useSocket";
import { ISupportTicket } from "@/interfaces/support.interface";
import {
  openFilePreviewInNewTab,
  splitImageFileName,
} from "@/lib/file-image.utils";
import {
  formatToCapitalizedWords,
  formatWithPadStart,
  renderFullName,
} from "@/lib/utils";
import {
  C_SupportApi,
  useAddCommentAsCustomerMutation,
  useGetCustomersSupportTicketsQuery,
} from "@/redux/api/customer-portal/support.query";
import {
  supportApi,
  useAddCommentToTicketMutation,
  useGetSupportTicketsQuery,
} from "@/redux/api/supportQuery";
import { TicketType } from "@/schemas/customer-portal/support.schema";
import {
  DAY_MONTH_YEAR_FORMAT,
  dateFormat,
  getRelativeTimeFromNow,
} from "@/utils/helper-functions/date-time.helper";
import { validateResponse } from "@/utils/helper-functions/form.helper";

interface TaskSheetProps {
  isOpen: boolean;
  toggleSheet: () => void;
  isInCustomerPortal?: boolean;
  name?: string;
}

export const InvoiceDetailCard = ({
  header,
  value,
}: {
  header: string;
  value: string;
}): JSX.Element => {
  return (
    <div className='grid grid-cols-4 py-2'>
      <span className='mr-[10px] text-[#667085] '>{header}</span>
      <span className='ml-[10px] col-span-2 text-left '>
        <span className='mr-2'>:</span> {value}
      </span>
    </div>
  );
};

const CommentCard = ({
  name,
  time,
  comment,
  isCustomer,
}: {
  name: string;
  time: string;
  comment: string;
  isCustomer: boolean;
}): JSX.Element => {
  return (
    <section className='p-[14px] border border-input rounded '>
      <section>
        <div className='flex items-center justify-between font-medium leading-6'>
          <h2 className='text-[16px] flex'>
            {name}
            {isCustomer && (
              <Badge className='ml-2 py-[2px] mb-2' variant={"primaryLight"}>
                Customer
              </Badge>
            )}
          </h2>
          <p className='text-[#6E6B7B] text-sm'>{time}</p>
        </div>
        <p className='max-w-[80%] text-sm text-black/80'>{comment}</p>
      </section>
    </section>
  );
};

const AttachmentCard = ({ url }: { url: string }): JSX.Element => {
  const fileExtension = url.split(".").pop() || "pdf";
  const fileName = splitImageFileName(url, imgBaseUrl);

  return (
    <>
      {!allowedImageTypes.includes(fileExtension) ? (
        <div
          className='flex flex-col items-center gap-1 p-1 bg-white rounded-lg cursor-pointer'
          onClick={() => openFilePreviewInNewTab(url)}
        >
          <div className='w-16 h-16'>
            <FileIcon
              extension={fileExtension}
              //@ts-expect-error - defaultStyles[fileExtension] is not assignable to type 'CSSProperties'
              {...defaultStyles[fileExtension]}
            />
          </div>
          <a
            target='_blank'
            rel='noopener noreferrer'
            className='w-full mt-3 overflow-hidden text-xs leading-tight text-center underline text-primary line-clamp-2'
            title={fileName}
          >
            {fileName}
          </a>
        </div>
      ) : (
        <div className='flex flex-col items-center gap-1 p-1 bg-white rounded-lg'>
          <Avatar
            name={"CARD"}
            src={url || placeholderImg}
            className='!h-20 !w-20 object-cover rounded-lg'
          />

          <span
            className='w-full overflow-hidden text-xs leading-tight text-center text-gray-700 line-clamp-2'
            title={fileName}
          >
            {fileName}
          </span>
        </div>
      )}
    </>
  );
};

const TaskSheet = ({
  isOpen,
  toggleSheet,
  isInCustomerPortal = false,
}: TaskSheetProps): JSX.Element => {
  const { socket } = useSocket("/");
  const { invalidateTags } = useApiInvalidator<
    typeof supportApi | typeof C_SupportApi
  >();
  const { id, reset } = useQueryID(SupportTicketKeys.TICKET_ID);
  const [comment, setIsComment] = React.useState<string>("");

  const { data, isLoading } = useGetSupportTicketsQuery(
    { id },
    {
      skip: !isOpen || isInCustomerPortal || !id,
    },
  );

  const {
    data: ticketDataInCustomerPortal,
    isLoading: isTicketDataFetchingInCustomerPortal,
  } = useGetCustomersSupportTicketsQuery(
    {
      ticketId: id,
    },
    {
      skip: !isOpen || !id || !isInCustomerPortal,
    },
  );

  const ticketData =
    (data?.data as ISupportTicket) ||
    (ticketDataInCustomerPortal?.data as ISupportTicket);
  const [handleAddCommentInWorkspace, { isLoading: isCommentPosting }] =
    useAddCommentToTicketMutation();
  const [handleAddCommentAsCustomer, { isLoading: isCustomerCommentPosting }] =
    useAddCommentAsCustomerMutation();
  const handleSheetClose = (): void => {
    reset();
    toggleSheet();
  };

  const renderTicketNumber = (): JSX.Element => {
    if (ticketData?.type === TicketType.DOCKET) {
      return (
        <InvoiceDetailCard
          header='Docket No'
          value={formatWithPadStart({
            value: ticketData?.docket?.docketId,
            length: 2,
            padChar: "0",
            prefix: "WD-",
          })}
        />
      );
    } else if (ticketData?.type === TicketType.INVOICE) {
      return (
        <InvoiceDetailCard
          header='Invoice No'
          value={ticketData?.invoice?.invoiceNumber ?? "-"}
        />
      );
    } else {
      return (
        <InvoiceDetailCard
          header='Ticket No'
          value={ticketData?.ticketNo ?? "-"}
        />
      );
    }
  };

  const handleAddComment = async (): Promise<void> => {
    if (!comment) {
      toast.error("Please enter a comment");

      return;
    }

    const data = {
      description: comment,
    };

    const action = isInCustomerPortal
      ? handleAddCommentAsCustomer
      : handleAddCommentInWorkspace;

    const response = await action({
      data,
      method: REQUEST_METHODS.POST,
      ticketId: id,
    });

    validateResponse({
      response,
      onSuccess: () => {
        toast.success("Comment added successfully");
        setIsComment("");
      },
    });
  };

  const handleKeyUp = async (
    e: React.KeyboardEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (e.key === "Enter") {
      await handleAddComment();
    }
  };

  React.useMemo(() => {
    if (isOpen && id) {
      socket?.on(
        SOCKET_KEYS.SUPPORT_COMMENT,
        (data: { action: SocketEventType; supportTicket: string }) => {
          if (data.supportTicket === id) {
            if (isInCustomerPortal) {
              invalidateTags(C_SupportApi, ["support"]);
            } else {
              invalidateTags(supportApi, ["support"]);
            }
          }
        },
      );
    }
  }, [socket, id, isOpen, invalidateTags, isInCustomerPortal]);

  return (
    <Sheet.Sheet open={isOpen} onOpenChange={handleSheetClose}>
      <Sheet.SheetContent className='min-w-[40dvw] sidebar_scrollbar '>
        {isLoading || isTicketDataFetchingInCustomerPortal ? (
          <section className='space-y-2'>
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className=' h-80' />
            ))}
          </section>
        ) : (
          <ScrollArea className='h-[calc(100vh-4rem)] sidebar_scrollbar'>
            <section className='px-3 space-y-5 sidebar_scrollbar mb-14'>
              <Sheet.SheetHeader className='flex-row gap-2 text-[22px] font-semibold leading-5'>
                {SupportConstants.TASK_SHEET.SUPPORT_TICKET}:{" "}
                <span>{ticketData?.ticketNo}</span>
              </Sheet.SheetHeader>
              <section className='p-3 space-y-2 border rounded border-input'>
                {renderTicketNumber()}
                <InvoiceDetailCard
                  header='Project'
                  value={ticketData?.project?.name}
                />
                <InvoiceDetailCard
                  header='Customer'
                  value={ticketData?.customer?.name ?? "-"}
                />
                <InvoiceDetailCard
                  header='Created At'
                  value={
                    ticketData?.createdAt
                      ? dateFormat(ticketData?.createdAt, DAY_MONTH_YEAR_FORMAT)
                      : "-"
                  }
                />
                <InvoiceDetailCard
                  header='Type'
                  value={formatToCapitalizedWords(ticketData?.type ?? "")}
                />
                <InvoiceDetailCard
                  header='Created By'
                  value={ticketData?.createdBy ?? "-"}
                />
                {ticketData?.type === TicketType.GENERAL && (
                  <div className='grid items-start grid-cols-4 py-2'>
                    <span className='mr-[10px] text-[#667085] '>
                      Attachments
                    </span>
                    <span className='ml-[10px] col-span-3 text-left flex items-start '>
                      <span className='mr-2'>:</span>{" "}
                      {ticketData?.documents?.length ? (
                        <div className='grid grid-cols-4 gap-1'>
                          {ticketData.documents.map((url, index) => (
                            <AttachmentCard key={index} url={url} />
                          ))}
                        </div>
                      ) : (
                        "-"
                      )}
                    </span>
                  </div>
                )}
                <div className='text-[#667085]'>Description</div>
                <div
                  className='editorContent'
                  dangerouslySetInnerHTML={{
                    __html: ticketData?.description ?? "-",
                  }}
                />
              </section>
              <h1 className='text-xl font-semibold'>
                {SupportConstants.TASK_SHEET.COMMENTS}
              </h1>
              <section className='space-y-[18px]'>
                {ticketData?.comments?.length ? (
                  ticketData.comments.map(
                    ({ id, user, customerUser, createdAt, description }) => {
                      const { firstName, lastName, middleName } =
                        user || customerUser || {};

                      return (
                        <CommentCard
                          key={id}
                          name={renderFullName({
                            params: { firstName, lastName, middleName },
                          })}
                          time={getRelativeTimeFromNow(createdAt)}
                          comment={description}
                          isCustomer={!!customerUser}
                        />
                      );
                    },
                  )
                ) : (
                  <p>No comments found</p>
                )}
              </section>
              <div className='fixed bottom-0 w-[36%] bg-white dark:bg-primary py-5'>
                <div className='relative'>
                  <Input
                    placeholder={SupportConstants.TASK_SHEET.ADD_COMMENT}
                    className='w-full px-2 py-[20px] '
                    value={comment}
                    onChange={(e) => setIsComment(e.target.value)}
                    onKeyUp={handleKeyUp}
                  />
                  <Button
                    className='absolute transform -translate-y-1/2 right-1 top-1/2'
                    size={"sm"}
                    onClick={handleAddComment}
                    isLoading={isCommentPosting || isCustomerCommentPosting}
                  >
                    {SupportConstants.TASK_SHEET.SEND}
                  </Button>
                </div>
              </div>
            </section>
          </ScrollArea>
        )}
      </Sheet.SheetContent>
    </Sheet.Sheet>
  );
};

export default TaskSheet;
